import React, { useState, useEffect } from 'react';
import scheduleData from './scheduleData.json';

const days = ['Понеделник', 'Вторник', 'Сряда', 'Четвъртък', 'Петък'];

const dayEmojis = {
    'Понеделник': '🌙',
    'Вторник': '🔥',
    'Сряда': '⚡',
    'Четвъртък': '🌟',
    'Петък': '🎉'
};

function getLectureStatus(index, lecturesForDay, day) {
    const now = new Date();
    const currentDayName = days[now.getDay() - 1];

    if (day !== currentDayName) {
        return 'status-default';
    }

    const currentTime = now.getTime();
    const [start, end] = lecturesForDay[index].hour.split('-').map(time => {
        const [hours, minutes] = time.split(':').map(Number);
        return new Date().setHours(hours, minutes, 0, 0);
    });

    if (currentTime > end) return 'status-red';
    if (currentTime >= start && currentTime <= end) return 'status-green';

    if (index > 0) {
        const [prevStart, prevEnd] = lecturesForDay[index - 1].hour.split('-').map(time => {
            const [hours, minutes] = time.split(':').map(Number);
            return new Date().setHours(hours, minutes, 0, 0);
        });

        if (currentTime > prevEnd && currentTime < start) return 'status-orange';
    }

    return 'status-default';
}

export default function Schedule() {
    const [filteredSchedule, setFilteredSchedule] = useState(scheduleData.lectures);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDay, setSelectedDay] = useState('');
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'timeline'

    useEffect(() => {
        const filtered = scheduleData.lectures.filter(lecture =>
            (lecture.lecture.toLowerCase().includes(searchTerm.toLowerCase()) ||
                lecture.lecturer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                lecture.type.toLowerCase().includes(searchTerm.toLowerCase())) &&
            (selectedDay === '' || lecture.day === selectedDay)
        );
        setFilteredSchedule(filtered);
    }, [searchTerm, selectedDay]);

    const now = new Date();
    const currentDayName = days[now.getDay() - 1];

    return (
        <div className="schedule-container">
            <div className="schedule-header">
                <h1>📚 Всички лекции</h1>
                <p className="schedule-subtitle">Преглед на седмичната програма</p>
            </div>

            <div className="schedule-controls">
                <div className="filters-wrapper">
                    <div className="search-box">
                        <span className="search-icon">🔍</span>
                        <input
                            type="text"
                            placeholder="Търсене по лекция, преподавател или тип..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                        {searchTerm && (
                            <button className="clear-search" onClick={() => setSearchTerm('')}>✕</button>
                        )}
                    </div>
                    
                    <select 
                        value={selectedDay} 
                        onChange={(e) => setSelectedDay(e.target.value)}
                        className="day-filter"
                    >
                        <option value="">📅 Всички дни</option>
                        {days.map(day => (
                            <option key={day} value={day}>
                                {dayEmojis[day]} {day}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="view-toggle">
                    <button 
                        className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                        onClick={() => setViewMode('grid')}
                        title="Grid View"
                    >
                        ▦
                    </button>
                    <button 
                        className={`view-btn ${viewMode === 'timeline' ? 'active' : ''}`}
                        onClick={() => setViewMode('timeline')}
                        title="Timeline View"
                    >
                        ☰
                    </button>
                </div>
            </div>

            <div className={`schedule-content ${viewMode}`}>
                {days.filter(day => filteredSchedule.some(lecture => lecture.day === day)).map(day => {
                    const lecturesForDay = filteredSchedule.filter(lecture => lecture.day === day);
                    const isToday = day === currentDayName;
                    let isOrangeAssigned = false;

                    return (
                        <div key={day} className={`day-section ${isToday ? 'today' : ''}`}>
                            <div className="day-header">
                                <div className="day-title">
                                    <span className="day-emoji">{dayEmojis[day]}</span>
                                    <h2>{day}</h2>
                                    {isToday && <span className="today-badge">Днес</span>}
                                </div>
                                <span className="lecture-count">{lecturesForDay.length} лекции</span>
                            </div>

                            <div className={viewMode === 'timeline' ? 'timeline-view' : 'schedule-grid'}>
                                {lecturesForDay.map((lecture, index) => {
                                    let lectureStatus = getLectureStatus(index, lecturesForDay, day);

                                    if (!isOrangeAssigned && lectureStatus === 'status-default' && index > 0) {
                                        const prevLectureStatus = getLectureStatus(index - 1, lecturesForDay, day);
                                        if (prevLectureStatus === 'status-green') {
                                            lectureStatus = 'status-orange';
                                            isOrangeAssigned = true;
                                        }
                                    }

                                    return (
                                        <div key={index} className={`lecture-card-new ${lectureStatus} type-${lecture.type.toLowerCase().replace(' ', '-')}`}>
                                            <div className="lecture-type-header">
                                                <span className={`lecture-type-badge ${lecture.type.toLowerCase().replace(' ', '-')}`}>
                                                    {lecture.type}
                                                </span>
                                            </div>
                                            
                                            <div className="lecture-content">
                                                <h3 className="lecture-title">{lecture.lecture}</h3>
                                                
                                                <div className="lecture-info">
                                                    <div className="info-item">
                                                        <span className="info-icon">👨‍🏫</span>
                                                        <span>{lecture.lecturer}</span>
                                                    </div>
                                                    <div className="info-item">
                                                        <span className="info-icon">🚪</span>
                                                        <span>Стая {lecture.roomNo}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="lecture-time">
                                                <span className="time-icon">🕐</span>
                                                <span className="time-text">{lecture.hour}</span>
                                            </div>

                                            <div className={`status-indicator ${lectureStatus}`}></div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            {filteredSchedule.length === 0 && (
                <div className="no-results">
                    <span className="no-results-icon">🔍</span>
                    <h3>Няма намерени лекции</h3>
                    <p>Опитайте различни критерии за търсене</p>
                </div>
            )}
        </div>
    );
}
